import { useState, useMemo, useEffect } from "react";
import { applyQuoteSelections, EMPTY_SELECTIONS } from "@/lib/quoteChoices";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Info } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { shouldIncludeInProgramme } from "@/lib/programmeFilterUtils";

const formSchema = z.object({
  programme_title: z.string().optional(),
  prepared_for: z.string().optional(),
  programme_message: z.string().optional(),
  terms_and_conditions: z.string().optional(),
  is_white_label: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

interface GenerateProgrammeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  lead: any;
  acceptedQuotationId: string | null;
  onProgrammeGenerated?: () => void;
}

export function GenerateProgrammeDialog({
  open,
  onOpenChange,
  leadId,
  lead,
  acceptedQuotationId,
  onProgrammeGenerated,
}: GenerateProgrammeDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      programme_title: lead?.title || "",
      prepared_for: "",
      programme_message: "",
      terms_and_conditions: "",
      is_white_label: false,
    },
  });

  // Keep the title in sync when the lead loads after the dialog mounts.
  useEffect(() => {
    if (!open) return;
    if (!form.getValues("programme_title")) {
      form.setValue("programme_title", lead?.title || "");
    }
  }, [open, lead?.title, form]);

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);

    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error("You must be logged in to generate a programme");
      }

      // First try to get items from accepted quote's snapshot
      let itemsToSnapshot: any[] = [];
      let layout: string = (lead as any)?.itinerary_layout || "day_by_day";
      let sections: any = (lead as any)?.itinerary_sections || [];
      
      if (acceptedQuotationId) {
        const { data: acceptedQuote } = await supabase
          .from("quotations")
          .select("itinerary_snapshot, itinerary_layout, itinerary_sections, client_selections")
          .eq("id", acceptedQuotationId)
          .single();
        
        if (acceptedQuote?.itinerary_snapshot && Array.isArray(acceptedQuote.itinerary_snapshot)) {
          // Keep only the client's chosen hotel/extras, then treat them as part of the programme.
          const sel = (acceptedQuote as any).client_selections?.optional_item_ids
            ? (acceptedQuote as any).client_selections
            : EMPTY_SELECTIONS;
          itemsToSnapshot = applyQuoteSelections(acceptedQuote.itinerary_snapshot as any[], sel).visible
            .map((i: any) => ({ ...i, pricing_treatment: "included", alternative_to_item_id: null }));
        }
        if ((acceptedQuote as any)?.itinerary_layout) layout = (acceptedQuote as any).itinerary_layout;
        if (Array.isArray((acceptedQuote as any)?.itinerary_sections) && (acceptedQuote as any).itinerary_sections.length)
          sections = (acceptedQuote as any).itinerary_sections;
      }

      // Fallback: fetch current included items if no snapshot
      if (itemsToSnapshot.length === 0) {
        const { data: items, error: itemsError } = await supabase
          .from("itinerary_items")
          .select("*")
          .eq("lead_id", leadId)
          .eq("include_in_quote", true)
          .order("date", { ascending: true, nullsFirst: false })
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true });

        if (itemsError) throw itemsError;

        // Fetch products and suppliers to enrich the snapshot
        const { data: products } = await supabase
          .from("products")
          .select("product_name, hero_image_url");

        const { data: suppliers } = await supabase
          .from("suppliers")
          .select("name, hero_image_url");

        itemsToSnapshot = (items || []).map((item) => {
          const matchedProduct = products?.find(
            (p) => p.product_name === item.title || p.product_name === item.description
          );
          const matchedSupplier = suppliers?.find((s) => s.name === item.supplier);

          return {
            ...item,
            products: matchedProduct || null,
            suppliers: matchedSupplier || null,
          };
        });
      }

      // Filter out non-content items (fees, surcharges, admin items)
      const totalBeforeFilter = itemsToSnapshot.length;
      itemsToSnapshot = itemsToSnapshot.filter(shouldIncludeInProgramme);
      const excludedCount = totalBeforeFilter - itemsToSnapshot.length;
      if (excludedCount > 0) {
        console.log(`Programme filter: excluded ${excludedCount} non-content item(s)`);
      }

      // Generate programme number
      const { data: programmeNumber, error: programmeNumberError } = await supabase
        .rpc("generate_programme_number");
      
      if (programmeNumberError) throw programmeNumberError;

      // Insert the programme (document_type = 'programme')
      const { data: newProgramme, error: insertError } = await supabase
        .from("quotations")
        .insert({
          lead_id: leadId,
          user_id: user.id,
          quote_number: programmeNumber,
          document_type: "programme",
          programme_title: values.programme_title || lead?.title || null,
          prepared_for: values.prepared_for?.trim() || null,
          programme_message: values.programme_message || null,
          terms_and_conditions: values.terms_and_conditions || null,
          is_white_label: values.is_white_label || false,
          status: "draft",
          // No financial data for programmes
          subtotal: 0,
          discount_percentage: 0,
          discount_amount: 0,
          markup_percentage: 0,
          markup_amount: 0,
          total_amount: 0,
          payment_status: null,
          itinerary_snapshot: itemsToSnapshot,
          itinerary_layout: layout,
          itinerary_sections: sections,
          pax_count: lead?.pax_count || null,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Log activity
      await supabase.from("activity_log").insert({
        lead_id: leadId,
        user_id: user.id,
        action: "programme_generated",
        details: `Travel programme ${programmeNumber} generated`,
      });

      queryClient.invalidateQueries({ queryKey: ["programmes", leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });

      toast.success(`Programme ${programmeNumber} generated successfully!`);
      onProgrammeGenerated?.();
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      console.error("Error generating programme:", error);
      toast.error(error.message || "Failed to generate programme");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Generate Travel Programme</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="p-4 bg-muted rounded-lg space-y-2 text-sm">
              <p className="text-muted-foreground">
                This will create a client-facing itinerary document without pricing information.
              </p>
              <p className="font-medium">
                Items included: {acceptedQuotationId ? "Based on the accepted quote" : "Based on current itinerary items"}
              </p>
              <div className="flex items-start gap-2 text-muted-foreground pt-1">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Fees, surcharges, and administrative items are automatically excluded from the programme.</p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="programme_title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Programme Title</FormLabel>
                  <FormControl>
                    <Input placeholder={lead?.title || "Enter title..."} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="prepared_for"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prepared For (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder={lead?.client_name || "Client name"} {...field} />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Leave blank to use the lead client name{lead?.client_name ? ` (${lead.client_name})` : ""}. Set this
                    when the document is for an end client.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="programme_message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Welcome Message (optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      rows={4} 
                      placeholder="Dear valued guest, we are delighted to share your travel programme..." 
                      {...field} 
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Programme Notes from the lead are included automatically — use this only for an extra intro.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="terms_and_conditions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Important Information / Travel Tips</FormLabel>
                  <FormControl>
                    <Textarea 
                      rows={3} 
                      placeholder="What to bring, meeting points, dress code, etc." 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_white_label"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">White Label</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Remove all branding (logo, company name, contact details) from the programme
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  "Generate Programme"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
